// Support Console domain logic: tones, tenant health and the issue rules.
//
// Everything here works on a normalised *tenant model* (see toTenantModel) so the same
// rules drive the Tenants table, Today, the palette and the tenant workspace. The list
// endpoint does not (yet) report WhatsApp state, owner sign-in or failed messages; those
// model fields stay `undefined` and render as "unknown" instead of being guessed. The
// workspace fills them from the per-tenant endpoints (users + state).

// Single source of truth for status colours. Static class strings so Tailwind's JIT sees them.
export const TONE = {
  ok: { dot: 'bg-green-600', tag: 'bg-green-50 text-green-800', text: 'text-green-800' },
  warn: { dot: 'bg-amber-600', tag: 'bg-amber-50 text-amber-800', text: 'text-amber-800' },
  crit: { dot: 'bg-red-600', tag: 'bg-red-50 text-red-800', text: 'text-red-800' },
  muted: { dot: 'bg-admin-400', tag: 'bg-admin-100 text-admin-700', text: 'text-admin-700' },
}

export const RANK = { crit: 3, warn: 2, muted: 1, ok: 0 }

const GB = 1024 ** 3
const DAY = 86400000

export const worstTone = (issues) =>
  issues.reduce((w, i) => (RANK[i.tone] > RANK[w] ? i.tone : w), 'ok')

export const BILLING_LABEL = {
  active: ['Em dia', 'ok'],
  trialing: ['Período de teste', 'muted'],
  past_due: ['Em atraso', 'crit'],
  awaiting_payment: ['Aguardando pagamento', 'warn'],
  canceled: ['Cancelado', 'muted'],
  suspended: ['Suspenso', 'crit'],
}

export const billingInfo = (m) => {
  const [label, tone] = BILLING_LABEL[m.billing] || BILLING_LABEL.active
  return { label, tone }
}

export const statusInfo = (m) => {
  if (!m.active) return { label: 'Suspenso', tone: 'crit' }
  if (m.billing === 'trialing' || m.subscriptionStatus === 'trial') return { label: 'Período de teste', tone: 'muted' }
  return { label: 'Ativo', tone: 'ok' }
}

// Tenant user roles as shown to support. Unknown roles fall back to the raw value.
const ROLE_LABEL = { owner: 'Proprietário', admin: 'Administrador', staff: 'Equipe' }
export const roleLabel = (role) => ROLE_LABEL[role] || role

// WAHA states → support language. `undefined` = not reported, `null` = no session.
export const WA_EXPLAIN = {
  WORKING: 'Sessão ativa e enviando mensagens.',
  CONNECTED: 'Sessão ativa e enviando mensagens.',
  FAILED: 'A sessão no servidor caiu. O celular do cliente ainda pode mostrar “conectado”.',
  SCAN_QR_CODE: 'A sessão está aguardando o cliente escanear um QR code.',
  STOPPED: 'A sessão está pausada. Nada será enviado até que seja reiniciada.',
  STARTING: 'A sessão está iniciando. Verifique novamente em um minuto.',
  UNREACHABLE: 'Não foi possível acessar o provedor de WhatsApp (WAHA), então o estado atual é desconhecido.',
}

export const waInfo = (m) => {
  if (m.wa === undefined) return { label: 'Desconhecido', tone: 'muted' }
  if (m.wa === null) return { label: 'Não configurado', tone: 'muted' }
  const s = m.wa.state
  if (s === 'WORKING' || s === 'CONNECTED') return { label: 'Conectado', tone: 'ok' }
  if (s === 'SCAN_QR_CODE') return { label: 'Requer leitura do QR', tone: 'warn' }
  if (s === 'STOPPED') return { label: 'Parado', tone: m.active ? 'crit' : 'muted' }
  if (s === 'STARTING') return { label: 'Iniciando', tone: 'muted' }
  if (s === 'UNREACHABLE' || !s) return { label: 'Desconhecido', tone: 'muted' }
  return { label: 'Desconectado', tone: 'crit' }
}

export const ownerInfo = (m) => {
  if (m.noOwner) return { label: 'Sem proprietário', tone: 'crit' }
  if (m.owner === undefined) return { label: 'Desconhecido', tone: 'muted' }
  if (m.owner === null) return { label: '—', tone: 'muted' }
  return m.owner.can
    ? { label: 'OK', tone: 'ok' }
    : { label: 'Bloqueado', tone: m.active ? 'crit' : 'muted' }
}

export const storageInfo = (m) => {
  const { used, limit } = m.storage
  const ratio = limit > 0 ? used / limit : 0
  const pct = Math.round(ratio * 100)
  const tone = pct >= 100 ? 'crit' : pct >= 90 ? 'warn' : 'ok'
  const usedGb = (used / GB).toFixed(1)
  const limitGb = limit > 0 ? `${+(limit / GB).toFixed(1)} GB` : '∞'
  return {
    ratio,
    pct,
    width: `${Math.min(100, pct)}%`,
    tone,
    text: `${usedGb} / ${limitGb}`,
    limitText: limitGb,
  }
}

const daysUntil = (iso) => (iso ? Math.ceil((new Date(iso) - Date.now()) / DAY) : null)
const daysSince = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso)) / DAY)) : null)

/**
 * Normalise a tenant payload (list row or detail) into the model the rules read.
 * `extras` carries what only the workspace knows: `staff` (tenant users) and
 * `state` (getTenantState().whatsapp).
 */
export const toTenantModel = (t, extras = {}) => {
  const sub = t.subscription || null
  const billing = t.billing_state || (t.is_active === false ? 'suspended' : 'active')

  // Owner sign-in: the workspace has the users list; the list payload may carry it later.
  let owner
  if (extras.staff) {
    const o = extras.staff.find((u) => u.role === 'owner')
    owner = o
      ? { name: o.name, email: o.email, can: !!o.can_login, reason: o.cannot_login_reason }
      : null
  } else if (t.owner_can_login !== undefined) {
    owner = { email: t.owner_email, can: !!t.owner_can_login, reason: t.owner_cannot_login_reason }
  }

  // WhatsApp: live state from the workspace, or list fields when the API reports them.
  let wa
  const state = extras.state
  if (state) {
    if (!state.session) wa = null
    else {
      const live = state.live_status || {}
      wa = {
        state: live.error ? 'UNREACHABLE' : live.state || live.status || null,
        lastSeen: state.session.last_seen_at || null,
        session: state.session.session_name,
        engine: state.session.engine,
        db: state.session.status,
        proxy: state.proxy
          ? `${state.proxy.proxy_address}:${state.proxy.port}${
              state.proxy.city_name || state.proxy.country_code
                ? ` · ${[state.proxy.city_name, state.proxy.country_code].filter(Boolean).join(', ')}`
                : ''
            }`
          : null,
        proxyValid: state.proxy ? !!state.proxy.valid : undefined,
      }
    }
  } else if (t.whatsapp_state !== undefined) {
    wa = t.whatsapp_state
      ? { state: t.whatsapp_state, lastSeen: t.whatsapp_last_seen || null, proxyValid: t.proxy_valid }
      : null
  }

  const program = t.program_affiliation
    ? {
        name: t.program_affiliation.program_name || 'Parceiro',
        days: t.program_affiliation.days_left,
        shielding: !!t.program_affiliation.shielding,
        endsAt: t.program_affiliation.ends_at,
      }
    : null

  return {
    id: t.id,
    name: t.name || t.slug,
    slug: t.slug,
    plan: t.subscription_tier ? t.subscription_tier[0].toUpperCase() + t.subscription_tier.slice(1) : 'Basic',
    tier: t.subscription_tier,
    subscriptionStatus: t.subscription_status,
    active: t.is_active !== false,
    reason: t.deactivation_reason,
    deactivatedAt: t.deactivated_at,
    ownerEmail: t.owner_email,
    // owner_email is only present on include_sensitive payloads, which every platform
    // payload is; treat a missing key the same as "no owner" only when the key exists.
    noOwner: 'owner_email' in t ? !t.owner_email : false,
    owner,
    wa,
    failed: t.failed_messages_24h,
    billing,
    pastDueDays: billing === 'past_due' ? daysSince(sub?.current_period_end) : null,
    trialDays: billing === 'trialing' ? daysUntil(t.trial_ends_at || sub?.current_period_end) : null,
    trialEndsAt: t.trial_ends_at,
    program,
    storage: {
      used: t.statistics?.storage_bytes || 0,
      limit: t.statistics?.storage_limit_bytes > 0 ? t.statistics.storage_limit_bytes : 0,
    },
    clients: t.statistics?.clients ?? 0,
    users: t.statistics?.users ?? 0,
    lastActive: t.last_activity_at || extras.lastActive || null,
    billingEmail: t.billing_email,
    domain: t.domain,
    timezone: t.timezone,
    createdAt: t.created_at,
    publicUrl: t.public_url,
    raw: t,
  }
}

// The `fix` copy encodes support procedure — review with the support lead before changing.
export const issuesOf = (m) => {
  const out = []
  if (!m.active) {
    out.push({
      tone: 'muted',
      title: 'Estúdio suspenso',
      detail: m.reason || 'Motivo não registrado',
      fix: 'Ninguém neste estúdio consegue entrar. Reative somente depois que a causa for resolvida; a reativação restaura o acesso imediatamente.',
      tab: 'billing',
    })
  }
  if (m.noOwner) {
    out.push({
      tone: 'crit',
      title: 'Sem usuário proprietário',
      detail: 'O estúdio não consegue entrar de forma alguma',
      fix: 'Um proprietário da plataforma deve definir o e-mail do proprietário em Editar estúdio. O convite é enviado automaticamente ao salvar.',
      tab: '',
    })
  } else if (m.owner && !m.owner.can && m.active) {
    out.push({
      tone: 'crit',
      title: 'O proprietário não consegue entrar',
      detail: m.owner.reason || 'Acesso bloqueado',
      fix: 'Peça para verificar a caixa de spam em busca do convite de no-reply@vestirstudio.com. Se não estiver lá, um proprietário da plataforma pode enviar um novo link de redefinição por esta página.',
      tab: 'access',
    })
  }
  if (m.wa) {
    const lastSeen = m.wa.lastSeen ? formatRelative(m.wa.lastSeen) : 'desconhecido'
    if (m.wa.state === 'FAILED' || (m.wa.state === 'STOPPED' && m.active)) {
      out.push({
        tone: 'crit',
        title: 'WhatsApp desconectado',
        detail: `Estado ${m.wa.state} · visto por último ${lastSeen}`,
        fix: 'Um celular mostrando “conectado” não significa que a sessão no servidor esteja. Escale um Reinício; se retornar SCAN_QR_CODE, o cliente escaneia novamente em Configurações → WhatsApp.',
        tab: 'whatsapp',
      })
    }
    if (m.wa.state === 'SCAN_QR_CODE') {
      out.push({
        tone: 'warn',
        title: 'WhatsApp aguardando leitura do QR code',
        detail: `Desde ${lastSeen}`,
        fix: 'Peça ao cliente para abrir Configurações → WhatsApp e escanear o QR code com o celular da empresa.',
        tab: 'whatsapp',
      })
    }
    if (m.wa.proxyValid === false) {
      out.push({
        tone: 'warn',
        title: 'Proxy inválido',
        detail: m.wa.proxy || 'O proxy atribuído falhou na validação',
        fix: 'Sessões em um proxy inválido reconectam mal. Atribua outro proxy em Proxies (proprietário).',
        tab: 'whatsapp',
      })
    }
  }
  if (m.failed > 0) {
    const many = m.failed > 10
    out.push({
      tone: many ? 'crit' : 'warn',
      title: `${m.failed} ${m.failed > 1 ? 'mensagens com falha' : 'mensagem com falha'} em 24h`,
      detail: many ? 'Principalmente erros de sessão' : 'Erros de destinatário',
      fix: many
        ? 'Estas falhas somem quando a sessão reconecta. Envios com falha não são repetidos automaticamente após 3 tentativas — informe ao cliente quais lembretes reenviar.'
        : 'Os números dos destinatários não estão no WhatsApp. Não há nada a corrigir na plataforma.',
      tab: 'whatsapp',
    })
  }
  if (m.billing === 'past_due') {
    out.push({
      tone: 'crit',
      title: 'Pagamento em atraso',
      detail: `${m.pastDueDays != null ? `${m.pastDueDays} dias · ` : ''}o acesso continua até o 14º dia`,
      fix: 'Peça ao cliente para atualizar o cartão em Configurações → Cobrança. Uma nova tentativa bem-sucedida normaliza o status em poucos minutos.',
      tab: 'billing',
    })
  }
  if (m.billing === 'awaiting_payment') {
    out.push({
      tone: 'warn',
      title: 'Aguardando o primeiro pagamento',
      detail: 'Checkout não concluído',
      fix: 'O link de checkout está no e-mail de boas-vindas. Os recursos ficam bloqueados até o pagamento.',
      tab: 'billing',
    })
  }
  if (m.billing === 'trialing' && m.trialDays != null && m.trialDays <= 3) {
    out.push({
      tone: 'warn',
      title: m.trialDays < 0 ? 'Período de teste encerrado' : `Período de teste termina em ${m.trialDays} dia${m.trialDays === 1 ? '' : 's'}`,
      detail: 'Nenhuma forma de pagamento cadastrada',
      fix: 'Mencione a data de término do período de teste se entrar em contato por qualquer outro motivo.',
      tab: 'billing',
    })
  }
  if (m.program && m.program.days != null && m.program.days < 30) {
    out.push({
      tone: 'warn',
      title:
        m.program.days < 0
          ? `Programa ${m.program.name} encerrado`
          : `Programa ${m.program.name} termina em ${m.program.days} dias`,
      detail: m.program.shielding ? 'Sem assinatura por trás' : 'Assinatura ativa',
      fix: 'Quando o programa termina sem assinatura, o estúdio passa para Aguardando pagamento.',
      tab: 'billing',
    })
  }
  const st = storageInfo(m)
  if (st.ratio >= 0.9) {
    out.push({
      tone: st.ratio >= 1 ? 'crit' : 'warn',
      title: `Armazenamento ${st.pct}% usado`,
      detail: `${st.text}`,
      fix: 'Os uploads param em 100%. Sugira arquivar conjuntos de fotos antigos ou fazer upgrade do plano.',
      tab: 'billing',
    })
  }
  return out.sort((a, b) => RANK[b.tone] - RANK[a.tone])
}

export const ISSUE_TAB_LABEL = { access: 'Verificar acesso', whatsapp: 'Abrir WhatsApp', billing: 'Abrir cobrança' }

/** Everything a list / palette / rail needs about one tenant. */
export const tenantView = (m) => {
  const issues = issuesOf(m)
  const worst = worstTone(issues)
  return {
    model: m,
    issues,
    worst,
    needsAttention: issues.length > 0 && worst !== 'muted',
    healthLabel: issues.length
      ? issues.length > 1
        ? `${issues[0].title} +${issues.length - 1}`
        : issues[0].title
      : 'Saudável',
    status: statusInfo(m),
    billing: billingInfo(m),
    wa: waInfo(m),
    owner: ownerInfo(m),
    storage: storageInfo(m),
  }
}

/** The five rail / context rows. Each row knows which workspace tab explains it. */
export const healthChecks = (v) => {
  const m = v.model
  const failedTone = m.failed > 10 ? 'crit' : m.failed > 0 ? 'warn' : m.failed === 0 ? 'ok' : 'muted'
  const access =
    v.owner.tone === 'ok'
      ? 'Proprietário OK'
      : m.noOwner
        ? 'Sem usuário proprietário'
        : m.owner
          ? m.owner.reason || 'Acesso bloqueado'
          : 'Não verificado'
  const billingValue =
    m.billing === 'past_due'
      ? `Em atraso${m.pastDueDays != null ? ` ${m.pastDueDays}d` : ''}`
      : m.billing === 'trialing' && m.trialDays != null
        ? `Teste · ${Math.max(0, m.trialDays)}d restantes`
        : v.billing.label
  return [
    { label: 'Acesso', value: access, tone: v.owner.tone, tab: 'access' },
    {
      label: 'WhatsApp',
      value: m.wa ? `${v.wa.label}${m.wa.lastSeen ? ` · ${formatRelative(m.wa.lastSeen)}` : ''}` : v.wa.label,
      tone: v.wa.tone,
      tab: 'whatsapp',
    },
    {
      label: 'Mensagens',
      value: m.failed > 0 ? `${m.failed} com falha em 24h` : m.failed === 0 ? 'Sem falhas' : 'Não informado',
      tone: failedTone,
      tab: 'whatsapp',
    },
    { label: 'Cobrança', value: billingValue, tone: v.billing.tone, tab: 'billing' },
    {
      label: 'Armazenamento',
      value: `${v.storage.pct}% de ${v.storage.limitText}`,
      tone: v.storage.tone,
      tab: 'billing',
    },
  ]
}

// ---- formatting ----

export const formatRelative = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const diff = Date.now() - d.getTime()
  if (diff < 60000) return 'Agora mesmo'
  if (diff < 3600000) return `há ${Math.floor(diff / 60000)} min`
  if (diff < DAY && d.getDate() === new Date().getDate()) return `há ${Math.floor(diff / 3600000)} h`
  const yesterday = new Date(Date.now() - DAY)
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem'
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString('pt-BR', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) })
}

export const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'

export const formatDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—'

export const formatTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''

export const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?'

/** Phone-friendly matching: ignores spaces, +, -, () once the query has >3 digits. */
export const makeMatcher = (query) => {
  const q = query.trim().toLowerCase()
  const qn = q.replace(/[\s()+-]/g, '')
  return (...fields) => {
    if (!q) return true
    const hay = fields.filter(Boolean).join(' ').toLowerCase()
    if (hay.includes(q)) return true
    return qn.length > 3 && hay.replace(/[\s()+-]/g, '').includes(qn)
  }
}

export const studioUrl = (m) => m.publicUrl || `https://${m.slug}.vestirstudio.com`

/**
 * A 404/405 means the endpoint isn't deployed yet (README "new backend work"). Cross-origin,
 * a missing route already fails the CORS preflight, which axios reports as a network error
 * with no response — treat that the same way.
 */
export const isMissingEndpoint = (err) =>
  [404, 405, 501].includes(err?.response?.status) || (!err?.response && err?.code === 'ERR_NETWORK')
